/*
  cart-drawer.js

  Behaviour for sections/cart-drawer.liquid.

  Only runs when Theme settings -> Cart -> "Mini cart style" is "Cart drawer",
  which sets <body data-cart-style="drawer">.

  Empire keeps its own add-to-cart fly-out (AddToCartFlyout in empire.js). We do
  not replace that code path -- the add itself, variant resolution, gift-card
  recipient handling and error reporting all keep working. Instead we listen for
  the `cartcount:update` event Empire fires immediately after it appends the
  fly-out, detach the fly-out node before it can paint, and open this drawer.
*/
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var SECTION_SELECTOR = '#shopify-section-' + SECTION_ID;
  var NOTE_DEBOUNCE = 600;

  function routes() {
    var r = (window.Theme && window.Theme.routes) || {};
    return {
      cart: r.cart_url || '/cart',
      add: r.cart_add_url || '/cart/add',
      change: r.cart_change_url || '/cart/change',
      update: (r.cart_url || '/cart') + '/update'
    };
  }

  function CartDrawer(sectionEl) {
    this.sectionEl = sectionEl;
    this.isOpen = false;
    this.lastItemCount = null;
    this.noteTimer = null;
    this.pendingNote = null;
    this.opener = null;
    this.requestToken = 0;

    this._onKeydown = this._onKeydown.bind(this);
    this._onCartCountUpdate = this._onCartCountUpdate.bind(this);

    this._bind();
  }

  /* ------------------------------------------------------------- elements */

  CartDrawer.prototype.root = function () {
    return this.sectionEl.querySelector('[data-cdrawer]');
  };

  CartDrawer.prototype.panel = function () {
    return this.sectionEl.querySelector('[data-cdrawer-panel]');
  };

  CartDrawer.prototype.body = function () {
    return this.sectionEl.querySelector('[data-cdrawer-body]');
  };

  /* --------------------------------------------------------------- wiring */

  CartDrawer.prototype._bind = function () {
    var self = this;

    // Delegated so everything keeps working after a re-render.
    this.sectionEl.addEventListener('click', function (event) {
      var target = event.target;

      if (target.closest('[data-cdrawer-close]')) {
        event.preventDefault();
        self.close();
        return;
      }

      var qtyButton = target.closest('[data-cdrawer-qty]');
      if (qtyButton) {
        event.preventDefault();
        self._stepQuantity(qtyButton);
        return;
      }

      var removeButton = target.closest('[data-cdrawer-remove]');
      if (removeButton) {
        event.preventDefault();
        self._setQuantity(removeButton.closest('[data-cdrawer-item]'), 0);
        return;
      }

    });

    this.sectionEl.addEventListener('change', function (event) {
      var input = event.target.closest('[data-cdrawer-qty-input]');
      if (!input) return;

      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) quantity = 1;
      self._setQuantity(input.closest('[data-cdrawer-item]'), quantity);
    });

    this.sectionEl.addEventListener('input', function (event) {
      if (!event.target.closest('[data-cdrawer-note]')) return;
      self._queueNoteSave(event.target);
    });

    // The header cart button opens the drawer instead of navigating to /cart.
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest(
        '[data-cart-drawer-open], .site-header-cart--button'
      );
      if (!trigger) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      self.open(trigger);
    });

    window.addEventListener('cartcount:update', this._onCartCountUpdate);
    document.addEventListener('keydown', this._onKeydown);
  };

  /* ---------------------------------------------------- add-to-cart bridge */

  CartDrawer.prototype._onCartCountUpdate = function (event) {
    var detail = event.detail || {};

    if (detail.source === 'cart-drawer') {
      this.lastItemCount = detail.item_count;
      return;
    }

    // Empire appends its fly-out to the header immediately before firing this
    // event. Detach it in the same tick so it never paints, then show ours.
    var stray = document.querySelector('[data-site-header] .atc-banner--container');
    var addedViaFlyout = Boolean(stray);
    if (stray) stray.remove();

    var previous = this.lastItemCount;
    this.lastItemCount = detail.item_count;

    if (document.body.classList.contains('template-cart')) return;

    var added = addedViaFlyout || (previous !== null && detail.item_count > previous);
    if (!added) return;

    var self = this;
    this.refresh().then(function () {
      self.open();
    });
  };

  /* ---------------------------------------------------------- open / close */

  CartDrawer.prototype.open = function (opener) {
    var root = this.root();
    if (!root || this.isOpen) return;

    this.opener = opener || document.activeElement;
    this.isOpen = true;

    root.setAttribute('data-cdrawer-open', 'true');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cdrawer-open');

    var panel = this.panel();
    var focusable = panel && panel.querySelector('[data-cdrawer-close]');
    if (focusable) focusable.focus();
  };

  CartDrawer.prototype.close = function () {
    var root = this.root();
    if (!root || !this.isOpen) return;

    this.isOpen = false;
    root.setAttribute('data-cdrawer-open', 'false');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cdrawer-open');

    var opener = this.opener;
    this.opener = null;

    // Empire's fly-out may also try to restore focus on the same interaction.
    // Deferring a frame makes sure ours is the one that sticks.
    if (opener && document.contains(opener)) {
      requestAnimationFrame(function () {
        opener.focus();
      });
    }
  };

  CartDrawer.prototype._onKeydown = function (event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    var panel = this.panel();
    if (!panel) return;

    var items = Array.prototype.filter.call(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, summary, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) {
        return (
          el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
        );
      }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* -------------------------------------------------------- cart mutations */

  CartDrawer.prototype._stepQuantity = function (button) {
    var item = button.closest('[data-cdrawer-item]');
    if (!item) return;

    var input = item.querySelector('[data-cdrawer-qty-input]');
    var current = parseInt(input && input.value, 10);
    if (isNaN(current)) current = 1;

    var next = current + parseInt(button.getAttribute('data-cdrawer-qty'), 10);
    if (next < 0) next = 0;

    this._setQuantity(item, next);
  };

  CartDrawer.prototype._setQuantity = function (item, quantity) {
    if (!item) return;

    var self = this;
    item.setAttribute('data-cdrawer-busy', '');
    this._setError('');

    this._flushNote()
      .then(function () {
        return self._post(routes().change + '.js', {
          id: item.getAttribute('data-cdrawer-key'),
          quantity: quantity
        });
      })
      .then(function (cart) {
        self._broadcast(cart);
        return self.refresh();
      })
      .catch(function (error) {
        item.removeAttribute('data-cdrawer-busy');
        self._setError(error.message);
      });
  };

  CartDrawer.prototype._queueNoteSave = function (textarea) {
    var self = this;

    this.pendingNote = textarea.value;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(function () {
      self._flushNote();
    }, NOTE_DEBOUNCE);

    this._setNoteStatus('');
  };

  /*
    Write any debounced note straight away. Cart mutations call this first so a
    re-render can never land between the customer typing and the note reaching
    the cart -- which would wipe what they just wrote out of the textarea.
  */
  CartDrawer.prototype._flushNote = function () {
    clearTimeout(this.noteTimer);

    if (this.pendingNote === null) return Promise.resolve();

    var self = this;
    var note = this.pendingNote;

    return this._post(routes().update + '.js', { note: note })
      .then(function () {
        if (self.pendingNote === note) self.pendingNote = null;
        self._setNoteStatus('Saved');
      })
      .catch(function () {
        self._setNoteStatus('Could not save note');
      });
  };

  CartDrawer.prototype._setNoteStatus = function (message) {
    var status = this.sectionEl.querySelector('[data-cdrawer-note-status]');
    if (status) status.textContent = message;
  };

  /* ---------------------------------------------------------------- render */

  /*
    Re-render through the Section Rendering API, but swap only the regions that
    change. Replacing the whole section would recreate the panel element and
    replay its slide-in transition on every quantity tap.
  */
  CartDrawer.prototype.refresh = function () {
    var self = this;
    var token = ++this.requestToken;
    var url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', SECTION_ID);

    var bodyEl = this.body();
    if (bodyEl) bodyEl.setAttribute('data-cdrawer-loading', '');

    return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not refresh the cart.');
        return response.json();
      })
      .then(function (data) {
        if (token !== self.requestToken) return;

        var parsed = new DOMParser().parseFromString(data[SECTION_ID], 'text/html');
        var fresh = parsed.querySelector('[data-cdrawer]');
        if (!fresh) return;

        self._swap(fresh);
      })
      .catch(function (error) {
        self._setError(error.message);
      })
      .then(function () {
        var current = self.body();
        if (current) current.removeAttribute('data-cdrawer-loading');
      });
  };

  CartDrawer.prototype._swap = function (fresh) {
    var root = this.root();
    if (!root) return;

    var oldBody = this.body();
    var scrollTop = oldBody ? oldBody.scrollTop : 0;

    // Preserve in-flight note editing across the swap.
    var oldNote = this.sectionEl.querySelector('[data-cdrawer-note]');
    var noteHadFocus = oldNote && document.activeElement === oldNote;
    var noteUnsaved = this.pendingNote !== null;
    var noteValue = noteHadFocus || noteUnsaved ? oldNote && oldNote.value : null;
    var noteStart = noteHadFocus ? oldNote.selectionStart : null;
    var notesWereOpen =
      noteHadFocus ||
      noteUnsaved ||
      Boolean(this.sectionEl.querySelector('.cdrawer__notes[open]'));

    var regions = ['[data-cdrawer-body]', '.cdrawer__footer', '.cdrawer__title'];
    regions.forEach(function (selector) {
      var target = root.querySelector(selector);
      var source = fresh.querySelector(selector);
      if (target && source) target.innerHTML = source.innerHTML;
    });

    root.setAttribute(
      'data-cdrawer-item-count',
      fresh.getAttribute('data-cdrawer-item-count')
    );

    var newBody = this.body();
    if (newBody) newBody.scrollTop = scrollTop;

    var notes = this.sectionEl.querySelector('.cdrawer__notes');
    if (notes && notesWereOpen) notes.open = true;

    var newNote = this.sectionEl.querySelector('[data-cdrawer-note]');
    if (newNote && noteValue !== null) {
      newNote.value = noteValue;

      if (noteHadFocus) {
        newNote.focus();
        if (noteStart !== null) newNote.setSelectionRange(noteStart, noteStart);
      }
    }
  };

  /* ----------------------------------------------------------------- utils */

  CartDrawer.prototype._setError = function (message) {
    var el = this.sectionEl.querySelector('[data-cdrawer-error]');
    if (el) el.textContent = message || '';
  };

  CartDrawer.prototype._post = function (url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Something went wrong.');
        }
        return data;
      });
    });
  };

  CartDrawer.prototype._getCart = function () {
    return fetch(routes().cart + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  };

  /* Keep the header bubble and any other listeners in sync. */
  CartDrawer.prototype._broadcast = function (cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;

    this.lastItemCount = cart.item_count;

    document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
      el.dataset.headerCartCount = cart.item_count > 0 ? cart.item_count : '';
      el.classList.toggle('visible', cart.item_count > 0);
    });

    window.dispatchEvent(
      new CustomEvent('cartcount:update', {
        detail: Object.assign({}, cart, { source: 'cart-drawer' })
      })
    );
  };

  /* ------------------------------------------------------------------ boot */

  function init() {
    if (document.body.getAttribute('data-cart-style') !== 'drawer') return;

    var sectionEl = document.querySelector(SECTION_SELECTOR);
    if (!sectionEl || sectionEl.cartDrawer) return;

    sectionEl.cartDrawer = new CartDrawer(sectionEl);
    window.cartDrawer = sectionEl.cartDrawer;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section wrapper.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId === SECTION_ID) init();
  });
})();
